/**
 * detectCrossDocConflicts — Contract C4 (TR backend + BL comparator + BR/BL
 * surfacing).
 *
 * Client-side, pure comparator that runs at capture time so the FSR sees
 * cross-document conflicts immediately (before the server's authoritative
 * `ConflictDetectionService` runs at commit). It MIRRORS the server rules but
 * does not replace them — the server finding is canonical; this one drives the
 * in-dialog UX:
 *
 *   - minor  → reuse `FieldComparisonDialog` "pick which value" UX (brief §2.6)
 *   - major  → red ALERT; BR contributes a `fail` to `verifyClearance.idScan`
 *   - info   → quiet annotation
 *
 * PURE: the caller fetches the active rule catalog from
 * `/v3/config/conflict-rules` (Contract C5, TR-owned) and passes it in. When no
 * rules are supplied the built-in `DEFAULT_CONFLICT_RULES` catalog is used so
 * the comparator is functional before the config seeds (the 40-check menu is
 * seeded ALL-as-catalog by TR; Bill toggles tiers — slot-1/§3 seed-pending).
 *
 * Severity tiers default to brief §2.6's worked examples:
 *   - DOB conflict, passport-number mismatch, surname/given-name mismatch → major
 *     ("anything that may indicate the IDs are non-genuine")
 *   - middle-name / initial / capitalization name diffs, address diffs → minor
 */

export type ConflictSeverity = 'minor' | 'major' | 'info';

/**
 * One conflict rule. Sourced from `/v3/config/conflict-rules`
 * (`ConflictRule{ruleId,label,scope,defaultSeverity,severity,isActive}` per C5).
 */
export interface ConflictRule {
  ruleId: string;
  label: string;
  /** Comparison category — see KNOWN_SCOPES. Drives which comparator runs. */
  scope: string;
  defaultSeverity: ConflictSeverity;
  /** Admin override of the tier; falls back to `defaultSeverity` when unset. */
  severity?: ConflictSeverity | null;
  isActive: boolean;
}

/** One document's value contributing to a finding. */
export interface ConflictDocValue {
  docId: string;
  value: string;
}

/** Emitted upward; BR surfaces major→ALERT, BL surfaces minor→pick. */
export interface ConflictFinding {
  ruleId: string;
  severity: ConflictSeverity;
  /** Human field/category the conflict is about ('Date of birth', 'Name', …). */
  field: string;
  /** The differing values, one per contributing doc. */
  values: ConflictDocValue[];
  /** docIds that contributed (superset convenience for BR). */
  docs: string[];
}

/**
 * A captured document reduced to the comparable fields. The capture pipeline
 * builds this from the extracted values (name parts, DOB, passport number,
 * address components). Missing fields are simply skipped by the comparators.
 */
export interface CapturedDocForConflict {
  /** Stable id of the source doc (tileId / docCode). */
  docId: string;
  /** Friendly label for messages ("Foreign Passport"). */
  label?: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  /** ISO YYYY-MM-DD preferred; compared date-normalized. */
  dob?: string | null;
  /** Passport / ID number used for the cross-ref counterfeit check. */
  idNumber?: string | null;
  passportNumber?: string | null;
  addr?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  gender?: string | null;
}

/** Comparison categories this comparator understands. */
export const KNOWN_SCOPES = {
  dob: 'dob',
  name: 'name',
  passportNumber: 'passportNumber',
  idNumber: 'idNumber',
  address: 'address',
  gender: 'gender',
} as const;

/**
 * Built-in default catalog — mirrors the server's `ConflictDetectionService`
 * baseline + brief §2.6 tiers. Used when the caller doesn't pass a config-
 * sourced catalog. Tiers here are the PROPOSED defaults; admin toggles override
 * via `severity`.
 */
export const DEFAULT_CONFLICT_RULES: ReadonlyArray<ConflictRule> = [
  { ruleId: 'dob-mismatch',           label: 'Date of birth differs between documents', scope: 'dob',            defaultSeverity: 'major', isActive: true },
  { ruleId: 'name-surname-mismatch',  label: 'Surname / given name differs',            scope: 'name',           defaultSeverity: 'major', isActive: true },
  { ruleId: 'passport-number-mismatch', label: 'Passport number differs between docs',  scope: 'passportNumber', defaultSeverity: 'major', isActive: true },
  { ruleId: 'id-number-mismatch',     label: 'ID number differs between documents',     scope: 'idNumber',       defaultSeverity: 'major', isActive: true },
  { ruleId: 'name-minor-diff',        label: 'Middle name / initial / capitalization',  scope: 'name',           defaultSeverity: 'minor', isActive: true },
  { ruleId: 'address-mismatch',       label: 'Address differs between documents',       scope: 'address',        defaultSeverity: 'minor', isActive: true },
  { ruleId: 'gender-mismatch',        label: 'Sex / gender differs between documents',  scope: 'gender',         defaultSeverity: 'minor', isActive: true },
];

// ── normalization helpers ────────────────────────────────────────────────────

function clean(s: string | null | undefined): string {
  return (s ?? '').toString().trim();
}
function lower(s: string | null | undefined): string {
  return clean(s).toLowerCase();
}
/** Collapse punctuation + whitespace for loose comparison. */
function loose(s: string | null | undefined): string {
  return lower(s).replace(/[.,'#-]/g, '').replace(/\s+/g, ' ').trim();
}
/** Normalize a DOB to YYYY-MM-DD when parseable; else loose string. */
function normDob(s: string | null | undefined): string {
  const v = clean(s);
  if (!v) return '';
  const t = Date.parse(v);
  if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
  return loose(v);
}

const sev = (r: ConflictRule): ConflictSeverity => r.severity ?? r.defaultSeverity;

/**
 * Generic "do these documents disagree on a single value" check. Collects the
 * non-empty normalized values across docs; if 2+ DISTINCT values exist, emits
 * a finding listing each doc's raw value.
 */
function compareSingleValue(
  rule: ConflictRule,
  field: string,
  captures: ReadonlyArray<CapturedDocForConflict>,
  rawOf: (c: CapturedDocForConflict) => string,
  normOf: (raw: string) => string,
): ConflictFinding | null {
  const present = captures
    .map((c) => ({ docId: c.docId, raw: clean(rawOf(c)) }))
    .filter((x) => x.raw !== '');
  if (present.length < 2) return null;
  const distinct = new Set(present.map((x) => normOf(x.raw)));
  if (distinct.size < 2) return null;
  return {
    ruleId: rule.ruleId,
    severity: sev(rule),
    field,
    values: present.map((x) => ({ docId: x.docId, value: x.raw })),
    docs: present.map((x) => x.docId),
  };
}

/** Full name string from parts. */
function fullName(c: CapturedDocForConflict): string {
  return [c.firstName, c.middleName, c.lastName].map((p) => clean(p)).filter(Boolean).join(' ');
}
/** Surname+given only (ignores middle) — drives the major-vs-minor name split. */
function coreName(c: CapturedDocForConflict): string {
  return [c.firstName, c.lastName].map((p) => loose(p)).filter(Boolean).join(' ');
}
/** Composite address string from components. */
function fullAddress(c: CapturedDocForConflict): string {
  return [c.addr, c.city, c.state, c.zip].map((p) => clean(p)).filter(Boolean).join(', ');
}

/**
 * Detect cross-document conflicts. Pure + deterministic. Findings are ordered
 * major → minor → info, then by ruleId, for stable rendering.
 */
export function detectCrossDocConflicts(
  captures: ReadonlyArray<CapturedDocForConflict>,
  rules: ReadonlyArray<ConflictRule> = DEFAULT_CONFLICT_RULES,
): ConflictFinding[] {
  if (!captures || captures.length < 2) return [];
  const active = rules.filter((r) => r.isActive);
  const findings: ConflictFinding[] = [];

  for (const rule of active) {
    switch (rule.scope) {
      case KNOWN_SCOPES.dob: {
        const f = compareSingleValue(rule, 'Date of birth', captures, (c) => clean(c.dob), normDob);
        if (f) findings.push(f);
        break;
      }
      case KNOWN_SCOPES.passportNumber: {
        const f = compareSingleValue(rule, 'Passport number', captures, (c) => clean(c.passportNumber), loose);
        if (f) findings.push(f);
        break;
      }
      case KNOWN_SCOPES.idNumber: {
        const f = compareSingleValue(rule, 'ID number', captures, (c) => clean(c.idNumber), loose);
        if (f) findings.push(f);
        break;
      }
      case KNOWN_SCOPES.gender: {
        const f = compareSingleValue(rule, 'Sex / gender', captures, (c) => clean(c.gender), lower);
        if (f) findings.push(f);
        break;
      }
      case KNOWN_SCOPES.address: {
        const f = compareSingleValue(rule, 'Address', captures, fullAddress, loose);
        if (f) findings.push(f);
        break;
      }
      case KNOWN_SCOPES.name: {
        // Two-tier name comparison. A 'major' rule fires only when the CORE
        // name (surname + given) disagrees — the counterfeit signal. A 'minor'
        // rule fires when the full name differs but the core name agrees
        // (middle-name / initial / capitalization only).
        const named = captures.filter((c) => fullName(c) !== '');
        if (named.length < 2) break;
        const coreDistinct = new Set(named.map((c) => coreName(c)));
        const fullDistinct = new Set(named.map((c) => loose(fullName(c))));
        const isMajorRule = sev(rule) === 'major';
        const coreDisagrees = coreDistinct.size >= 2;
        const onlyMiddleOrCaseDiffers = !coreDisagrees && fullDistinct.size >= 2;
        if (isMajorRule && coreDisagrees) {
          findings.push({
            ruleId: rule.ruleId,
            severity: sev(rule),
            field: 'Name',
            values: named.map((c) => ({ docId: c.docId, value: fullName(c) })),
            docs: named.map((c) => c.docId),
          });
        } else if (!isMajorRule && onlyMiddleOrCaseDiffers) {
          findings.push({
            ruleId: rule.ruleId,
            severity: sev(rule),
            field: 'Name',
            values: named.map((c) => ({ docId: c.docId, value: fullName(c) })),
            docs: named.map((c) => c.docId),
          });
        }
        break;
      }
      default:
        // Unknown scope — server may define rules this client build doesn't
        // mirror yet. Skip silently; the server finding remains canonical.
        break;
    }
  }

  const rank: Record<ConflictSeverity, number> = { major: 0, minor: 1, info: 2 };
  findings.sort((a, b) => (rank[a.severity] - rank[b.severity]) || a.ruleId.localeCompare(b.ruleId));
  return findings;
}

/** Convenience: true when any finding is major (BR uses for the Verify fail). */
export function hasMajorConflict(findings: ReadonlyArray<ConflictFinding>): boolean {
  return findings.some((f) => f.severity === 'major');
}
